import { ActionIcon, Group, Loader, Tooltip, VisuallyHidden } from "@mantine/core";
import { CircleAlert, Smile, WandSparkles } from "lucide-react";
import type { ReactNode } from "react";

import classes from "./scripts.module.css";

/** Live's words (`script-generation/actions`, `script-header-actions`). */
export const GENERATE_SCRIPT = "Generate Script";
export const ENHANCE_SCRIPT = "Enhance Script";
export const SCRIPT_FAILED = "Failed to generate script... Please, try again";

export interface ScriptActionsProps {
  /** The wand: off while a script, an enhancement or the audio is under way. */
  canGenerate: boolean;
  /** The smiley: also off until the script is long enough. */
  canEnhance: boolean;
  /** The smiley's start is on its way: it spins. */
  enhancing: boolean;
  /** The last script failed: live's red mark with its words. */
  failed: boolean;
  onGenerate: () => void;
  onEnhance: () => void;
}

/** A disabled button takes no hover, so its tooltip hangs from a wrapper. */
function Tipped({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip label={label} withArrow>
      <span>{children}</span>
    </Tooltip>
  );
}

/**
 * Live's script tools beside a script's title (captures script-tools-224/01 to 03): a red mark
 * when the last script failed, the wand ("Generate Script") and the smiley ("Enhance Script").
 */
export function ScriptActions(props: ScriptActionsProps) {
  const { canGenerate, canEnhance, enhancing, failed, onGenerate, onEnhance } = props;
  return (
    <Group gap={10} wrap="nowrap">
      {failed ? (
        <Tooltip label={SCRIPT_FAILED} withArrow>
          <span className={classes.failed}>
            <CircleAlert size={16} aria-hidden />
            <VisuallyHidden>{SCRIPT_FAILED}</VisuallyHidden>
          </span>
        </Tooltip>
      ) : null}
      <Tipped label={GENERATE_SCRIPT}>
        <ActionIcon
          variant="subtle"
          size={36}
          aria-label={GENERATE_SCRIPT}
          disabled={!canGenerate}
          onClick={onGenerate}
        >
          <WandSparkles size={16} aria-hidden />
        </ActionIcon>
      </Tipped>
      <Tipped label={ENHANCE_SCRIPT}>
        <ActionIcon
          variant="subtle"
          size={36}
          aria-label={ENHANCE_SCRIPT}
          loading={enhancing}
          disabled={!canEnhance}
          onClick={onEnhance}
        >
          <Smile size={16} aria-hidden />
        </ActionIcon>
      </Tipped>
    </Group>
  );
}

/** Live's loader over the script while a script or an enhancement is written. */
export function ScriptBusy({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <div className={classes.busy} data-busy={busy || undefined}>
      {children}
      {busy ? (
        <span className={classes.loader}>
          <Loader size="md" aria-label="Writing the script" />
        </span>
      ) : null}
    </div>
  );
}
